/* eslint react-hooks/exhaustive-deps: "warn" */
import { type CSSProperties, useRef } from 'react';

import cn from 'classnames';

import { useInt } from 'hooks/useInt';
import { useIntersection } from 'hooks/useIntersection';

import { Button } from 'ui/atoms/Button/Button';
import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';

import styles from './TechnologyRegis.module.scss';

type Translate = (key: string) => string;

type Row = {
  label: string;
  regis: string;
  regisBold?: boolean;
  assistant: string;
};

const getRows = (t: Translate): Row[] => [
  { label: t('Commercial contracts'), regis: t('One'), assistant: t('Two') },
  { label: t('Data processing agreements'), regis: t('One'), assistant: t('Two') },
  { label: t('Assistant seat licences'), regis: t('None'), assistant: t('Required') },
  { label: t('Pricing basis'), regis: t('Per result'), regisBold: true, assistant: t('Per token consumed') },
  { label: t('Bulk retrieval'), regis: t('Direct, no inference'), assistant: t('Via the model context window') },
  { label: t('Data path'), regis: t('Single supplier'), assistant: t('Crosses a third party') },
  {
    label: t('Where the citation lands'),
    regis: t("On the register's own reference, per field"),
    regisBold: true,
    assistant: t("On the provider's own dataset"),
  },
  {
    label: t('Fields a register does not publish'),
    regis: t('Returns null and names the silent register'),
    assistant: t('Not guaranteed'),
  },
  {
    label: t('Customer data in training'),
    regis: t('Excluded, set out in the DPA'),
    assistant: t('Governed by two sets of terms'),
  },
];

const RegisMark = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
    <circle cx="12" cy="4.5" r="1.6" fill="currentColor" stroke="none" />
    <circle cx="19.5" cy="9" r="1.6" fill="currentColor" stroke="none" />
    <circle cx="19.5" cy="16" r="1.6" fill="currentColor" stroke="none" />
    <circle cx="12" cy="20.5" r="1.6" fill="currentColor" stroke="none" />
    <circle cx="4.5" cy="16" r="1.6" fill="currentColor" stroke="none" />
    <circle cx="4.5" cy="9" r="1.6" fill="currentColor" stroke="none" />
    <circle cx="12" cy="12.5" r="2.4" fill="currentColor" stroke="none" />
    <path d="M12 6.1v3.9M17.9 9.9l-3.4 2M17.9 15.1l-3.4-2M12 18.9V15M6.1 15.1l3.4-2M6.1 9.9l3.4 2" />
  </svg>
);

const AssistantMark = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
    <path d="M4 6h16v9H9l-4 3.5V15H4z" strokeLinejoin="round" />
    <path d="M9.5 10h5M9.5 12.5h3" strokeLinecap="round" />
  </svg>
);

export const TechnologyRegis = () => {
  const { t } = useInt();
  const rows = getRows(t);
  const tblRef = useRef<HTMLDivElement>(null);
  const revealed = useIntersection(tblRef, { threshold: 0.15 });

  return (
    <section className={styles.root}>
      <div className={styles.glow} />

      <div className={styles.grid}>
        <div className={styles.gut}>
          <span>
            <i className={styles.gutLine} />
            regis
          </span>
        </div>

        <div className={styles.col}>
          <TitleBlock
            align="left"
            theme="dark"
            title={t('Regis, over the API')}
            description={t(
              'A domain-specific model built for company verification. It plans and executes multi-step queries across government registers and returns structured data, with every value carrying the register it came from, the filing reference and the moment it was read.',
            )}
          />

          <div className={styles.actions}>
            <Button type="white" size="lg" href="/regis">
              {t('Request an API key')}
            </Button>
            <Button type="white-outline" size="lg" href="https://api.globaldatabase.com/docs/v2/#setup">
              {t('Read the docs')}
            </Button>
          </div>

          <div className={cn(styles.tbl, revealed && styles.tblOn)} ref={tblRef}>
            <div className={styles.hd}>
              <span />
              <span className={styles.hdRegis}>
                <i className={styles.mark}>
                  <RegisMark />
                </i>
                Regis API
              </span>
              <span className={styles.hdAssistant}>
                <i className={styles.mark}>
                  <AssistantMark />
                </i>
                {t('Data provider + assistant')}
              </span>
            </div>

            {rows.map((row, i) => (
              <div className={styles.row} style={{ '--d': `${i * 45}ms` } as CSSProperties} key={row.label}>
                <span className={styles.lbl}>{row.label}</span>
                <span className={cn(styles.regis, row.regisBold && styles.regisBold)}>{row.regis}</span>
                <span className={styles.gen}>{row.assistant}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
